import React from "react";
import ReactDOM from "react-dom/client";

// ─────────────────────────────────────────────────────────────
// Capturador de errores: en vez de pantalla blanca, muestra el error
// ─────────────────────────────────────────────────────────────
const rootElement = document.getElementById("root")!;
const root = ReactDOM.createRoot(rootElement);

const esc = (s: any) => String(s ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ── Chunks obsoletos tras un deploy: si un pedazo de código no carga (salió versión
// nueva con la app abierta), recargamos SOLO una vez → evita la pantalla en blanco. ──
function esChunkError(x: any): boolean {
  const msg  = String(x?.message || x?.reason?.message || x?.reason || x || "");
  const name = String(x?.name || x?.reason?.name || "");
  // OJO: NO incluir "Failed to fetch" a secas — es un error de red genérico (Firebase,
  // subir foto, etc.) y recargar por eso bota al usuario al inicio en pleno uso.
  // Solo recargamos ante errores REALES de carga de código/chunks.
  return name === "ChunkLoadError" ||
    /Loading( CSS)? chunk [\w-]+ failed|Failed to fetch dynamically imported module|dynamically imported module|Importing a module script failed/i.test(msg);
}
function recargarPorVersionNueva(): boolean {
  try {
    const k = "arain_reload_chunk", last = Number(sessionStorage.getItem(k) || "0");
    if (Date.now() - last < 10000) return true;
    sessionStorage.setItem(k, String(Date.now()));
  } catch {}
  window.location.reload();
  return true;
}

function pintarError(titulo: string, detalle: string) {
  // Solo pintamos directo al DOM si React no logró montar nada
  if (rootElement.childElementCount > 0) return;
  rootElement.innerHTML = `
    <div style="min-height:100vh;background:#070c14;color:#e5edf7;font-family:system-ui,-apple-system,sans-serif;padding:20px;box-sizing:border-box">
      <div style="max-width:520px;margin:0 auto">
        <div style="font-size:44px;text-align:center;margin-top:24px">⚠️</div>
        <h2 style="color:#f97316;text-align:center;font-size:19px;margin:8px 0">Se produjo un error</h2>
        <p style="color:#94a3b8;font-size:13px;text-align:center;margin:0 0 12px">Sácale una captura a esto y envíamela 👇</p>
        <div style="background:#0d1520;border:1px solid #1e2a3a;border-radius:10px;padding:14px">
          <div style="color:#f87171;font-weight:700;font-size:14px;word-break:break-word">${esc(titulo)}</div>
          <pre style="color:#cbd5e1;font-size:11px;white-space:pre-wrap;word-break:break-word;margin:8px 0 0;font-family:ui-monospace,monospace">${esc(detalle)}</pre>
        </div>
        <button onclick="try{localStorage.clear()}catch(e){};location.reload()" style="width:100%;margin-top:16px;background:#f97316;color:#fff;border:0;border-radius:10px;padding:14px;font-size:15px;font-weight:700">Limpiar caché y reintentar</button>
      </div>
    </div>`;
}

// Errores globales (carga de módulos, async, fuera de React)
window.addEventListener("error", (e) => {
  if (esChunkError(e)) { recargarPorVersionNueva(); return; }
  pintarError(e.message || "Error", (e.error && e.error.stack) || `${e.filename}:${e.lineno}:${e.colno}`);
});
window.addEventListener("unhandledrejection", (e) => {
  const r: any = e.reason;
  if (esChunkError(e)) { recargarPorVersionNueva(); return; }
  pintarError("Promesa rechazada: " + (r?.message || r), r?.stack || String(r));
});

// Errores durante el render de React
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: any }> {
  state = { error: null as any };
  static getDerivedStateFromError(error: any) { return { error }; }
  componentDidCatch(error: any) { if (esChunkError(error)) recargarPorVersionNueva(); }
  render() {
    const e = this.state.error;
    if (!e) return this.props.children as any;
    if (esChunkError(e)) return null;   // se está recargando por versión nueva
    return (
      <div style={{ minHeight: "100vh", background: "#070c14", color: "#e5edf7", fontFamily: "system-ui,-apple-system,sans-serif", padding: 20, boxSizing: "border-box" }}>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <div style={{ fontSize: 44, textAlign: "center", marginTop: 24 }}>⚠️</div>
          <h2 style={{ color: "#f97316", textAlign: "center", fontSize: 19, margin: "8px 0" }}>Se produjo un error</h2>
          <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", margin: "0 0 12px" }}>Sácale una captura a esto y envíamela 👇</p>
          <div style={{ background: "#0d1520", border: "1px solid #1e2a3a", borderRadius: 10, padding: 14 }}>
            <div style={{ color: "#f87171", fontWeight: 700, fontSize: 14, wordBreak: "break-word" }}>{String(e?.message || e)}</div>
            <pre style={{ color: "#cbd5e1", fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: "8px 0 0" }}>{String(e?.stack || "")}</pre>
          </div>
          <button onClick={() => { try { localStorage.clear(); } catch { } window.location.reload(); }} style={{ width: "100%", marginTop: 16, background: "#f97316", color: "#fff", border: 0, borderRadius: 10, padding: 14, fontSize: 15, fontWeight: 700 }}>Limpiar caché y reintentar</button>
        </div>
      </div>
    );
  }
}

// Import dinámico de App para poder capturar errores de su carga
import("./App")
  .then(({ default: App }) => {
    root.render(
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    );
  })
  .catch((err) => { if (esChunkError(err)) { recargarPorVersionNueva(); return; } pintarError("Error al cargar la app", err?.stack || String(err)); });
